/**
 * Input — pure controlled text-input atom.
 * Atom purity: imports only react + tokens.
 * Props flow in; no hooks, no API, no platform.
 *
 * Focus state: terracotta focus ring via box-shadow (S.focusRing).
 * Error state: danger border + focus ring in danger color.
 */
import { CSSProperties, useState } from 'react';
import { P, S, T } from '../../styles/tokens';

export interface InputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'tel' | 'numeric' | 'search';
  error?: string;
  autoFocus?: boolean;
}

function buildInputStyle(focused: boolean, hasError: boolean): CSSProperties {
  const borderColor = hasError
    ? P.danger
    : focused
      ? P.terracotta
      : S.separatorOpaque;
  const shadow = hasError
    ? `0 0 0 3px rgba(209,67,67,0.18)`
    : focused
      ? `0 0 0 3px ${S.focusRing}`
      : 'none';

  return {
    width: '100%',
    height: 48,
    borderRadius: 12,
    border: `1.5px solid ${borderColor}`,
    background: P.card,
    color: P.ink,
    fontFamily: T.fontBody,
    fontSize: 16,
    fontWeight: 400,
    padding: '0 14px',
    boxSizing: 'border-box',
    outline: 'none',
    boxShadow: shadow,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };
}

const errorTextStyle: CSSProperties = {
  marginTop: 4,
  font: T.footnote,
  color: P.danger,
};

export function Input({
  value,
  onChange,
  placeholder,
  type = 'text',
  inputMode,
  error,
  autoFocus,
}: InputProps) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        type={type}
        inputMode={inputMode}
        autoFocus={autoFocus}
        style={buildInputStyle(focused, !!error)}
        aria-invalid={error ? true : undefined}
      />
      {error && <span style={errorTextStyle}>{error}</span>}
    </div>
  );
}
